import React, { useState, useEffect, useCallback } from 'react';
import { getRunningStatus, removeStrategyFromBot, pauseStrategy, resumeStrategy, resetStrategy, getBotDiagnostics, getBotLiveDetail } from '../api';
import StrategyParamsLabel from './StrategyParamsLabel';
import BacktestDetail from './BacktestDetail';
import TickerDetail from './TickerDetail';

const C = {
  bg:      '#F4F6FB',
  card:    '#FFFFFF',
  border:  '#E2E8F0',
  primary: '#2563EB',
  success: '#16A34A',
  danger:  '#DC2626',
  warning: '#D97706',
  textLg:  '#0F172A',
  textMd:  '#374151',
  textSm:  '#6B7280',
};

function fmt(v, d = 2)  { if (v == null) return '—'; const n = Number(v); return isNaN(n) ? '—' : n.toFixed(d); }
function fmtUsd(v)       { if (v == null) return '—'; const n = Number(v); return isNaN(n) ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`; }
function fmtPct(v, plus) { if (v == null) return '—'; const n = Number(v); if (isNaN(n)) return '—'; return `${plus && n > 0 ? '+' : ''}${n.toFixed(2)}%`; }
function pctColor(v)     { if (v == null) return C.textMd; return Number(v) >= 0 ? C.success : C.danger; }

function fmtStartedAt(ts) {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function elapsed(startTs) {
  if (!startTs) return '—';
  const s = Math.max(0, Date.now() / 1000 - startTs);
  if (s < 60)   return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s/60)}m`;
  const h = Math.floor(s/3600), m = Math.floor((s%3600)/60);
  return h < 24 ? `${h}h${m}m` : `${Math.floor(h/24)}d${h%24}h`;
}

// Section header (used by BtBadges below)
function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px',
      color: C.textSm, marginTop: 8, marginBottom: 3,
    }}>{children}</div>
  );
}

// ── Backtest results ───────────────────────────────────────────────────────────
function BtBadges({ bm }) {
  if (!bm) return null;
  const items = [
    { k: 'win_rate',           label: 'WR(BT)',    f: v => `${fmt(v,1)}%`,  color: v => v >= 60 ? C.success : C.warning },
    { k: 'monthly_return_pct', label: 'Mo%(BT)',   f: v => fmtPct(v, true), color: pctColor },
    { k: 'yearly_return',      label: 'Yr%(BT)',   f: v => fmtPct(v, true), color: pctColor },
    { k: 'total_return',       label: 'Total(BT)', f: v => fmtPct(v, true), color: pctColor },
    { k: 'sharpe_ratio',       label: 'Sharpe',    f: v => fmt(v,2),         color: v => v >= 1 ? C.success : C.warning },
    { k: 'max_drawdown',       label: 'MaxDD',     f: v => `${fmt(v,1)}%`,  color: v => v < 20 ? C.success : C.danger },
    { k: 'num_trades',         label: 'Trades(BT)',f: v => String(v),        color: () => C.textMd },
    { k: 'vs_buyhold_monthly', label: 'vs B&H/mo', f: v => fmtPct(v, true), color: pctColor },
    { k: 'final_balance',      label: 'Final(BT)', f: v => fmtUsd(v),        color: () => C.textMd },
  ].filter(({ k }) => bm[k] != null);
  if (!items.length) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <SectionLabel>Результати бектесту</SectionLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {items.map(({ k, label, f, color }) => (
          <span key={k} style={{
            background: '#F8FAFC', border: `1px solid ${C.border}`,
            borderRadius: 4, padding: '1px 7px', fontSize: 10.5, fontFamily: 'monospace',
          }}>
            <span style={{ color: C.textSm }}>{label}:</span>
            <span style={{ fontWeight: 700, color: color(bm[k]), marginLeft: 2 }}>{f(bm[k])}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Diagnostics panel ─────────────────────────────────────────────────────────
function DiagnosticsPanel({ diag }) {
  if (!diag) return (
    <div style={{ padding: '10px 14px', color: C.textSm, fontSize: 12 }}>
      Запустіть бота — дані з'являться після першого циклу (~1 хв)
    </div>
  );

  const { x_buy, bot_status, seed_progress,
          last_cycle, cycles_24h, total_cycles,
          n_tickers_tracked, n_above_threshold, n_below_threshold, n_no_data,
          top_candidates = [], positions = [], recent_events = [],
          history_info = null } = diag;

  // ── Status banner ─────────────────────────────────────────────────────────
  if (bot_status === 'seeding_history') {
    const done  = seed_progress?.done  ?? 0;
    const total = seed_progress?.total ?? 0;
    const pct   = total > 0 ? Math.round(done / total * 100) : 0;
    return (
      <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 8, paddingTop: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 12, height: 12, borderRadius: '50%',
            border: `2px solid ${C.primary}`, borderTopColor: 'transparent',
            animation: 'spin 1s linear infinite',
          }} />
          <span style={{ fontWeight: 600, color: C.textMd, fontSize: 12 }}>
            Завантаження цінової історії...
          </span>
          <span style={{ fontSize: 12, color: C.textSm }}>
            {done} / {total} тікерів ({pct}%)
          </span>
        </div>
        <div style={{ marginTop: 8, background: '#F1F5F9', borderRadius: 4, height: 6, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: C.primary, width: `${pct}%`, transition: 'width 0.5s' }} />
        </div>
        <div style={{ fontSize: 11, color: C.textSm, marginTop: 6 }}>
          Перший торговий цикл почнеться після завершення завантаження.
          Зазвичай 3–8 хвилин залежно від кількості тікерів.
        </div>
      </div>
    );
  }

  if (bot_status === 'waiting_market_open') {
    return (
      <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 8, paddingTop: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>🕐</span>
          <div>
            <div style={{ fontWeight: 600, color: C.textMd, fontSize: 12 }}>Ринок закрито</div>
            <div style={{ fontSize: 11, color: C.textSm }}>
              Бот активний, очікує відкриття NYSE (09:30–16:00 ET, пн–пт).
              Торговий цикл запуститься автоматично.
            </div>
          </div>
        </div>
        {total_cycles > 0 && (
          <div style={{ fontSize: 11, color: C.textSm, marginTop: 6 }}>
            Всього циклів: {total_cycles} · Циклів за 24h: {cycles_24h}
          </div>
        )}
      </div>
    );
  }

  if (bot_status === 'initializing') {
    return (
      <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 8, paddingTop: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 10, height: 10, borderRadius: '50%',
            border: `2px solid ${C.textSm}`, borderTopColor: 'transparent',
            animation: 'spin 1s linear infinite',
          }} />
          <span style={{ fontSize: 12, color: C.textSm }}>Ініціалізація бота...</span>
        </div>
      </div>
    );
  }

  if (bot_status === 'fetching_prices') {
    return (
      <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 8, paddingTop: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 12, height: 12, borderRadius: '50%',
            border: `2px solid ${C.primary}`, borderTopColor: 'transparent',
            animation: 'spin 1s linear infinite',
          }} />
          <span style={{ fontWeight: 600, color: C.textMd, fontSize: 12 }}>
            Завантаження live цін...
          </span>
          <span style={{ fontSize: 11, color: C.textSm }}>
            1-й цикл: батчевий запит до yfinance (~20-40 сек). Наступні цикли — з кешу (5 хв TTL).
          </span>
        </div>
        {total_cycles > 0 && (
          <div style={{ fontSize: 11, color: C.textSm, marginTop: 6 }}>
            Всього циклів: {total_cycles}
          </div>
        )}
      </div>
    );
  }

  const lastTs = last_cycle?.ts ? new Date(last_cycle.ts).toLocaleTimeString() : '—';
  const lastBar = last_cycle?.bar ?? '—';
  const nSells  = last_cycle?.n_sells ?? 0;
  const nBuys   = last_cycle?.n_buys  ?? 0;

  const pctColor = (pct, threshold) => {
    if (pct >= threshold) return C.success;
    const ratio = pct / threshold;
    if (ratio >= 0.8) return C.warning;
    if (pct < 0)      return C.danger;
    return C.textSm;
  };

  return (
    <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 8, paddingTop: 10 }}>

      {/* ── Cycle health ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>
        {[
          { label: 'Останній цикл', value: lastTs },
          { label: 'Бар #',         value: total_cycles ?? lastBar },
          { label: 'Циклів за 24h', value: cycles_24h ?? '—' },
          { label: 'Тікерів',       value: n_tickers_tracked ?? '—' },
          { label: `≥${x_buy}% (сигнал)`, value: n_above_threshold ?? 0, color: n_above_threshold > 0 ? C.success : C.textSm },
          { label: 'Нижче порогу',  value: n_below_threshold ?? 0, color: C.textSm },
          { label: 'Без даних',     value: n_no_data ?? 0, color: C.textSm },
          { label: 'Покупки/цикл',  value: nBuys,  color: nBuys  > 0 ? C.success : C.textSm },
          { label: 'Продажі/цикл',  value: nSells, color: nSells > 0 ? C.warning  : C.textSm },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ textAlign: 'center', minWidth: 70 }}>
            <div style={{ fontSize: 9, color: C.textSm, marginBottom: 1 }}>{label}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: color || C.textMd }}>{value}</div>
          </div>
        ))}
      </div>

      {/* ── History window info ── */}
      {history_info && history_info.ref_price != null && (
        <div style={{ marginBottom: 10, padding: '6px 10px', background: '#F8FAFC', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 11 }}>
          <span style={{ color: C.textSm }}>Вікно порівняння ({history_info.sample_ticker}): </span>
          <span style={{ fontFamily: 'monospace', fontWeight: 600, color: C.textMd }}>
            реф. ${history_info.ref_price?.toFixed(2)} → поточна ${history_info.last_price?.toFixed(2)}
          </span>
          <span style={{ color: history_info.pct_in_window >= x_buy ? C.success : C.textSm, fontWeight: 700, marginLeft: 6, fontFamily: 'monospace' }}>
            {history_info.pct_in_window != null ? `${history_info.pct_in_window >= 0 ? '+' : ''}${history_info.pct_in_window?.toFixed(2)}%` : '—'}
          </span>
          <span style={{ color: C.textSm, marginLeft: 6 }}>
            (t_bars={history_info.t_bars}, потрібно ≥{x_buy}%, барів в історії: {history_info.n_bars})
          </span>
        </div>
      )}

      {/* ── Top buy candidates ── */}
      {top_candidates.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: C.textSm, marginBottom: 5 }}>
            Топ кандидати на купівлю (потрібно ≥{x_buy}%)
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ background: '#F8FAFC' }}>
                  {['Тікер','Ціна','% зміна','Потрібно','Різниця','Статус'].map(h => (
                    <th key={h} style={{ padding: '3px 8px', textAlign: 'left', fontWeight: 600, color: C.textSm, fontSize: 10, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {top_candidates.slice(0, 20).map((c, i) => {
                  const isBuy = c.buy;
                  const isNear = !isBuy && c.pct >= x_buy * 0.7;
                  return (
                    <tr key={c.ticker} style={{ background: i % 2 === 0 ? '#fff' : '#FAFBFC' }}>
                      <td style={{ padding: '3px 8px', fontWeight: 700, color: C.textLg, fontFamily: 'monospace' }}>{c.ticker}</td>
                      <td style={{ padding: '3px 8px', color: C.textMd, fontFamily: 'monospace' }}>${c.price?.toFixed(2) ?? '—'}</td>
                      <td style={{ padding: '3px 8px', fontWeight: 700, color: pctColor(c.pct, x_buy), fontFamily: 'monospace' }}>
                        {c.pct >= 0 ? '+' : ''}{c.pct?.toFixed(2)}%
                      </td>
                      <td style={{ padding: '3px 8px', color: C.textSm, fontFamily: 'monospace' }}>+{x_buy}%</td>
                      <td style={{ padding: '3px 8px', fontWeight: 600, color: c.gap >= 0 ? C.success : (isNear ? C.warning : C.textSm), fontFamily: 'monospace' }}>
                        {c.gap >= 0 ? '+' : ''}{c.gap?.toFixed(2)}%
                      </td>
                      <td style={{ padding: '3px 8px' }}>
                        {isBuy
                          ? <span style={{ background: '#DCFCE7', color: C.success, padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>▶ BUY</span>
                          : isNear
                            ? <span style={{ background: '#FEF9C3', color: C.warning, padding: '1px 6px', borderRadius: 4, fontSize: 10 }}>близько</span>
                            : <span style={{ color: C.textSm, fontSize: 10 }}>{c.reason?.substring(0, 20)}</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Open positions sell status ── */}
      {positions.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: C.textSm, marginBottom: 5 }}>
            Відкриті позиції — статус продажу
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ background: '#F8FAFC' }}>
                  {['Тікер','Куплено','Зараз','P&L%','Годин','Рішення','Причина'].map(h => (
                    <th key={h} style={{ padding: '3px 8px', textAlign: 'left', fontWeight: 600, color: C.textSm, fontSize: 10, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {positions.map((p, i) => (
                  <tr key={p.ticker} style={{ background: i % 2 === 0 ? '#fff' : '#FAFBFC' }}>
                    <td style={{ padding: '3px 8px', fontWeight: 700, color: C.textLg, fontFamily: 'monospace' }}>{p.ticker}</td>
                    <td style={{ padding: '3px 8px', color: C.textMd, fontFamily: 'monospace' }}>${p.buy_price?.toFixed(2)}</td>
                    <td style={{ padding: '3px 8px', color: C.textMd, fontFamily: 'monospace' }}>${p.price?.toFixed(2)}</td>
                    <td style={{ padding: '3px 8px', fontWeight: 700, fontFamily: 'monospace', color: p.pnl_pct >= 0 ? C.success : C.danger }}>
                      {p.pnl_pct >= 0 ? '+' : ''}{p.pnl_pct?.toFixed(2)}%
                    </td>
                    <td style={{ padding: '3px 8px', color: C.textSm, fontFamily: 'monospace' }}>
                      {p.hours_held != null ? `${p.hours_held.toFixed(1)}h` : `${p.bars_held}b`}
                    </td>
                    <td style={{ padding: '3px 8px' }}>
                      {p.sell
                        ? <span style={{ background: '#FEE2E2', color: C.danger, padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>SELL</span>
                        : <span style={{ background: '#F0FDF4', color: C.success, padding: '1px 6px', borderRadius: 4, fontSize: 10 }}>HOLD</span>
                      }
                    </td>
                    <td style={{ padding: '3px 8px', color: C.textSm, fontSize: 10 }}>{p.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Recent trade events ── */}
      {recent_events.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: C.textSm, marginBottom: 5 }}>
            Останні угоди ({recent_events.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {recent_events.map((e, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 11, padding: '2px 0' }}>
                <span style={{ color: C.textSm, fontSize: 10, minWidth: 60 }}>
                  {e.ts ? new Date(e.ts).toLocaleTimeString() : '—'}
                </span>
                <span style={{
                  padding: '1px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, minWidth: 34, textAlign: 'center',
                  background: e.action === 'BUY' ? '#DCFCE7' : '#FEE2E2',
                  color:      e.action === 'BUY' ? C.success  : C.danger,
                }}>{e.action}</span>
                <span style={{ fontWeight: 700, color: C.textLg, fontFamily: 'monospace', minWidth: 50 }}>{e.ticker}</span>
                <span style={{ color: C.textSm }}>×{e.qty} @ ${e.price?.toFixed(2)}</span>
                {e.pnl_pct != null && (
                  <span style={{ fontWeight: 600, color: e.pnl_pct >= 0 ? C.success : C.danger, fontFamily: 'monospace' }}>
                    {e.pnl_pct >= 0 ? '+' : ''}{e.pnl_pct?.toFixed(2)}%
                  </span>
                )}
                <span style={{ color: C.textSm, fontSize: 10 }}>{e.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {top_candidates.length === 0 && positions.length === 0 && recent_events.length === 0 && (
        <div style={{ color: C.textSm, fontSize: 12, padding: '8px 0' }}>
          Дані збираються... Зачекайте один цикл (~1 хв після запуску)
        </div>
      )}
    </div>
  );
}


// ── Strategy card ──────────────────────────────────────────────────────────────
function StrategyRow({ bot, onPause, onResume, onDelete, onReset, diagData, onShowCharts }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReset,  setConfirmReset]  = useState(false);
  const [acting,    setActing]    = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showDiag,  setShowDiag]  = useState(false);
  const isRunning = bot.running;
  const isPaused  = bot.is_paused;

  async function doDelete() {
    setActing(true);
    try { await onDelete(bot.name); }
    finally { setActing(false); setConfirmDelete(false); }
  }
  async function doPause() {
    setActing(true);
    try { await onPause(bot.name); }
    finally { setActing(false); }
  }
  async function doResume() {
    setActing(true);
    try { await onResume(bot.name); }
    finally { setActing(false); }
  }
  async function doReset() {
    setResetting(true);
    try { await onReset(bot.name); }
    finally { setResetting(false); setConfirmReset(false); }
  }

  const borderColor = isRunning ? C.primary : isPaused ? '#F59E0B' : '#CBD5E1';

  return (
    <div style={{
      background: C.card,
      border: `1px solid ${C.border}`,
      borderLeft: `3px solid ${borderColor}`,
      borderRadius: 8,
      padding: '10px 14px',
    }}>
      {/* Top row: info + buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'start' }}>
      {/* Left: all info */}
      <div>
        {/* Row 1: name + status + score + started */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: C.textLg }}>{bot.name}</span>
          {isRunning
            ? <span style={{ fontSize: 10, fontWeight: 600, background: '#DCFCE7', color: C.success, padding: '1px 7px', borderRadius: 9 }}>● live</span>
            : isPaused
              ? <span style={{ fontSize: 10, fontWeight: 600, background: '#FEF3C7', color: '#92400E', padding: '1px 7px', borderRadius: 9 }}>⏸ paused</span>
              : <span style={{ fontSize: 10, fontWeight: 600, background: '#F1F5F9', color: C.textSm, padding: '1px 7px', borderRadius: 9 }}>stopped</span>
          }
          {bot.score != null && (
            <span style={{ fontSize: 10, fontWeight: 600, background: '#EDE9FE', color: '#5B21B6', padding: '1px 7px', borderRadius: 9 }}>
              score {fmt(bot.score, 1)}
            </span>
          )}
          <span style={{ fontSize: 10.5, color: C.textSm }}>
            ▶ {fmtStartedAt(bot.started_at)}
            <span style={{ marginLeft: 4, color: '#94A3B8' }}>({elapsed(bot.started_at)})</span>
          </span>
          <span style={{ fontSize: 10.5, color: C.textSm }}>
            capital: <strong style={{ color: C.textMd }}>{fmtUsd(bot.capital)}</strong>
          </span>
        </div>

        {/* Row 2: live metrics grid */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 2 }}>
          {[
            { label: 'Portfolio',   value: fmtUsd(bot.total_value),                                              color: C.textLg  },
            { label: 'P&L',         value: fmtPct(bot.pnl_pct, true),                                           color: pctColor(bot.pnl_pct),         sub: (bot.pnl_usd >= 0 ? '+' : '') + fmtUsd(bot.pnl_usd) },
            { label: 'Mo/live',     value: fmtPct(bot.live_monthly_pct, true),                                   color: pctColor(bot.live_monthly_pct) },
            { label: 'Day/live',    value: fmtPct(bot.live_daily_pct, true),                                     color: pctColor(bot.live_daily_pct)   },
            { label: 'Win Rate',    value: bot.num_trades > 0 ? `${fmt(bot.win_rate,1)}%` : '—',                 color: bot.win_rate >= 60 ? C.success : bot.num_trades > 0 ? C.warning : C.textSm },
            { label: 'Trades',      value: `${bot.num_trades ?? 0} (${bot.closed_trades ?? 0} closed)`,          color: C.textMd },
            { label: 'Open pos',    value: bot.open_positions ?? 0,                                              color: C.textMd  },
            { label: 'Cash',        value: fmtUsd(bot.cash),                                                     color: C.textSm  },
            { label: 'Days',        value: bot.live_days != null ? fmt(bot.live_days, 2) : '—',                  color: C.textSm },
          ].map(({ label, value, color, sub }) => (
            <div key={label} style={{ textAlign: 'center', minWidth: 65 }}>
              <div style={{ fontSize: 9.5, color: C.textSm, marginBottom: 1 }}>{label}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color }}>{value}</div>
              {sub && <div style={{ fontSize: 9.5, color: C.textSm }}>{sub}</div>}
            </div>
          ))}
        </div>

        {/* Backtest results */}
        <BtBadges bm={bot.backtest} />

        {/* Full params */}
        <StrategyParamsLabel params={bot.params} />
      </div>

      {/* Right: buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', paddingTop: 2 }}>
        <button
          onClick={() => onShowCharts && onShowCharts(bot.name)}
          title="Показати графіки та угоди"
          style={{
            padding: '4px 11px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            background: C.card, color: C.primary,
            border: `1px solid ${C.primary}`,
            whiteSpace: 'nowrap',
          }}
        >📊 Графіки</button>
        <button
          onClick={() => setShowDiag(v => !v)}
          style={{
            padding: '4px 11px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            background: showDiag ? '#EFF6FF' : C.card,
            color: showDiag ? C.primary : C.textSm,
            border: `1px solid ${showDiag ? C.primary : C.border}`,
            whiteSpace: 'nowrap',
          }}
        >{showDiag ? '▲ Діагностика' : '▼ Діагностика'}</button>
        {/* Reset button — only for running strategies */}
        {!confirmReset && !confirmDelete && isRunning && (
          <button
            onClick={() => setConfirmReset(true)}
            title="Скинути на початковий капітал (видалити trades+SQLite, перезапустити)"
            style={{
              padding: '4px 11px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              background: '#FEF3C7', color: C.warning,
              border: `1px solid #FDE68A`, whiteSpace: 'nowrap',
            }}
          >↻ Reset</button>
        )}
        {confirmReset && (
          <>
            <button onClick={doReset} disabled={resetting}
              style={{ padding: '4px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: C.warning, color: '#fff', border: 'none', whiteSpace: 'nowrap' }}>
              {resetting ? '...' : '✓ Reset'}
            </button>
            <button onClick={() => setConfirmReset(false)}
              style={{ padding: '4px 10px', borderRadius: 5, fontSize: 11, cursor: 'pointer', background: '#F1F5F9', color: C.textSm, border: `1px solid ${C.border}` }}>
              Скасувати
            </button>
          </>
        )}

        {/* Pause / Resume / Delete buttons */}
        {!confirmReset && !confirmDelete && (
          isRunning ? (
            <button onClick={doPause} disabled={acting}
              title="Зупинити торгівлю (без видалення), можна відновити"
              style={{
                padding: '4px 11px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: '#FEF3C7', color: '#92400E',
                border: `1px solid #FDE68A`, whiteSpace: 'nowrap',
              }}
            >{acting ? '...' : '⏸ Пауза'}</button>
          ) : isPaused ? (
            <button onClick={doResume} disabled={acting}
              title="Відновити торгівлю"
              style={{
                padding: '4px 11px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: '#DCFCE7', color: C.success,
                border: `1px solid #86EFAC`, whiteSpace: 'nowrap',
              }}
            >{acting ? '...' : '▶ Відновити'}</button>
          ) : null
        )}

        {/* Delete (with confirm) */}
        {!confirmReset && !confirmDelete && (
          <button
            onClick={() => setConfirmDelete(true)}
            title="Видалити стратегію назовсіди"
            style={{
              padding: '4px 11px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              background: '#FEF2F2', color: C.danger,
              border: `1px solid #FECACA`, whiteSpace: 'nowrap',
            }}
          >✕ Видалити</button>
        )}
        {confirmDelete && (
          <>
            <button onClick={doDelete} disabled={acting}
              style={{ padding: '4px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: C.danger, color: '#fff', border: 'none', whiteSpace: 'nowrap' }}>
              {acting ? '...' : '✓ Видалити'}
            </button>
            <button onClick={() => setConfirmDelete(false)}
              style={{ padding: '4px 10px', borderRadius: 5, fontSize: 11, cursor: 'pointer', background: '#F1F5F9', color: C.textSm, border: `1px solid ${C.border}` }}>
              Скасувати
            </button>
          </>
        )}
      </div>
      </div>{/* end top-row grid */}

      {/* Diagnostics panel — full width */}
      {showDiag && <DiagnosticsPanel diag={diagData} />}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
const _spinStyle = `@keyframes spin { to { transform: rotate(360deg); } }`;

export default function RunningBots() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [lastUpd, setLastUpd] = useState(null);
  const [diagMap, setDiagMap] = useState({});  // name → diag object
  const [chartsModal, setChartsModal] = useState({ open: false, name: null, data: null, loading: false, error: null });
  const [tickerView,  setTickerView]  = useState(null); // ticker detail inside charts modal

  const fetchStatus = useCallback(async () => {
    try {
      const res = await getRunningStatus();
      setData(res.data);
      setError(null);
      setLastUpd(new Date());
    } catch { setError('Помилка завантаження'); }
    finally { setLoading(false); }
  }, []);

  const fetchDiag = useCallback(async () => {
    try {
      const res = await getBotDiagnostics();
      const map = {};
      for (const b of (res.data?.bots || [])) {
        map[b.name] = b;
      }
      setDiagMap(map);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchDiag();
    const id1 = setInterval(fetchStatus, 10000);
    const id2 = setInterval(fetchDiag, 15000);
    return () => { clearInterval(id1); clearInterval(id2); };
  }, [fetchStatus, fetchDiag]);

  async function handlePause(name) {
    try {
      await pauseStrategy(name);
      await fetchStatus();
      window.dispatchEvent(new Event('bot:changed'));
    } catch (e) {
      alert(`Помилка паузи: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function handleResume(name) {
    try {
      await resumeStrategy(name);
      await fetchStatus();
      window.dispatchEvent(new Event('bot:changed'));
    } catch (e) {
      alert(`Помилка відновлення: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function handleDelete(name) {
    try {
      await removeStrategyFromBot(name);
      await fetchStatus();
      window.dispatchEvent(new Event('bot:changed'));
    } catch (e) {
      alert(`Помилка видалення: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function handleReset(name) {
    try {
      const res = await resetStrategy(name);
      await fetchStatus();
      window.dispatchEvent(new Event('bot:changed'));
      const fresh = res?.data?.fresh_capital;
      alert(`Стратегію '${name}' скинуто. Новий капітал: $${fresh?.toLocaleString() || '?'}`);
    } catch (e) {
      alert(`Помилка reset: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function handleShowCharts(name) {
    setChartsModal({ open: true, name, data: null, loading: true, error: null });
    try {
      const res = await getBotLiveDetail(name);
      setChartsModal({ open: true, name, data: res.data, loading: false, error: null });
    } catch (e) {
      setChartsModal({ open: true, name, data: null, loading: false, error: 'Помилка завантаження даних' });
    }
  }

  function closeCharts() {
    setChartsModal({ open: false, name: null, data: null, loading: false, error: null });
    setTickerView(null);
  }

  const bots        = data?.bots || [];
  const running     = data?.running ?? false;
  const dataSource  = data?.data_source ?? 'yfinance';
  // Human-readable label що віддзеркалює бекенд-конфігурацію (MOCK_DATA_SOURCE).
  // На сервері з shared IB Gateway = "ibkr (delayed paper feed)".
  const sourceLabel = dataSource === 'ibkr'
    ? 'IBKR (delayed paper feed)'
    : dataSource === 'yfinance'
      ? 'yfinance (live)'
      : dataSource;

  return (
    <div style={{ padding: '18px 22px', maxWidth: 1200, margin: '0 auto' }}>
      <style>{_spinStyle}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <span style={{ fontSize: 18, fontWeight: 700, color: C.textLg }}>Тестування стратегій</span>
          <span style={{ fontSize: 12, color: C.textSm, marginLeft: 10 }}>Mock mode · {sourceLabel}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastUpd && <span style={{ fontSize: 11, color: C.textSm }}>updated {lastUpd.toLocaleTimeString()}</span>}
          {running
            ? <span style={{ background: '#DCFCE7', color: C.success, fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12 }}>
                ● {bots.filter(b => b.running).length} active
              </span>
            : <span style={{ background: '#F1F5F9', color: C.textSm, fontSize: 11, padding: '3px 10px', borderRadius: 12 }}>no active</span>
          }
          <button onClick={fetchStatus} style={{ padding: '5px 12px', borderRadius: 5, fontSize: 11, cursor: 'pointer', background: C.card, border: `1px solid ${C.border}`, color: C.textMd }}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Summary bar */}
      {running && bots.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center' }}>
          {[
            { label: 'Capital',    value: fmtUsd(data.total_capital) },
            { label: 'Value',      value: fmtUsd(data.total_value) },
            { label: 'Total P&L',  value: `${data.total_pnl_pct >= 0 ? '+' : ''}${fmt(data.total_pnl_pct, 2)}%`, color: pctColor(data.total_pnl_pct), sub: `${data.total_pnl_usd >= 0 ? '+' : ''}${fmtUsd(data.total_pnl_usd)}` },
            { label: 'Strategies', value: bots.length },
          ].map(({ label, value, color, sub }) => (
            <div key={label}>
              <div style={{ fontSize: 10.5, color: C.textSm }}>{label}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: color || C.textLg }}>{value}</div>
              {sub && <div style={{ fontSize: 10.5, color: C.textSm }}>{sub}</div>}
            </div>
          ))}
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 50, color: C.textSm }}>Loading...</div>}
      {error   && <div style={{ textAlign: 'center', padding: 30, color: C.danger }}>{error}</div>}

      {!loading && !error && bots.length === 0 && (
        <div style={{ textAlign: 'center', padding: '50px 20px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, color: C.textSm, fontSize: 13 }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🤖</div>
          <div style={{ fontWeight: 600, color: C.textMd, marginBottom: 5 }}>No running strategies</div>
          <div>Go to <strong>Стратегії</strong> and click <strong>▶ Start</strong> on any strategy row.</div>
        </div>
      )}

      {!loading && !error && bots.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {bots.map(bot => (
            <StrategyRow
              key={bot.name}
              bot={bot}
              onPause={handlePause}
              onResume={handleResume}
              onDelete={handleDelete}
              onReset={handleReset}
              diagData={diagMap[bot.name] ?? null}
              onShowCharts={handleShowCharts}
            />
          ))}
        </div>
      )}

      {/* ── Live charts modal overlay ── */}
      {chartsModal.open && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: '#F1F5F9', zIndex: 9999, overflowY: 'auto',
        }}>
          {chartsModal.loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 16 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid #2563EB', borderTopColor: 'transparent', animation: 'spin 1s linear infinite' }} />
              <span style={{ color: '#475569', fontSize: 14 }}>Завантаження даних…</span>
            </div>
          )}
          {chartsModal.error && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 12 }}>
              <span style={{ color: '#DC2626', fontSize: 14 }}>{chartsModal.error}</span>
              <button onClick={closeCharts} style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #E2E8F0', background: '#fff', cursor: 'pointer', fontSize: 13 }}>← Назад</button>
            </div>
          )}
          {!chartsModal.loading && !chartsModal.error && chartsModal.data && !tickerView && (
            <BacktestDetail
              data={{ ...chartsModal.data, name: chartsModal.name + ' · Live' }}
              onBack={closeCharts}
              onTickerClick={(ctx) => setTickerView(ctx)}
              onRecalc={null}
            />
          )}
          {!chartsModal.loading && !chartsModal.error && chartsModal.data && tickerView && (
            <TickerDetail
              ticker={{
                ticker: tickerView.ticker,
                name:   tickerView.ticker,
                daily:  { has_data: true },
                hourly: { has_data: true },
              }}
              tradeContext={tickerView}
              onBack={() => setTickerView(null)}
            />
          )}
        </div>
      )}
    </div>
  );
}
